@if (salesOrder; as order) {
  <app-card-shell padding="none" class="block overflow-hidden">
    <div class="flex items-center justify-between px-6 pt-4 pb-2">
      <h2 class="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider m-0">
        {{ 'COMPONENTS.SALES_ORDERS.SUMMARY.TITLE' | translate }}
      </h2>

      @if (!isReadonly) {
        <button
          type="button"
          class="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-sm font-medium rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          [disabled]="isCreatingLine || !!editingLineId"
          (click)="startCreateLine()"
        >
          + {{ 'COMPONENTS.SALES_ORDERS.BUTTONS.ADD_LINE' | translate }}
        </button>
      }
    </div>

    @if (isReadonly) {
      <div
        class="mx-6 mb-6 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900 text-amber-700 dark:text-amber-400 text-sm"
        role="status"
      >
        {{ 'COMPONENTS.SALES_ORDERS.MESSAGES.LINES_LOCKED' | translate }}
      </div>
    }

    @if (lineModalErrorKey && !isCreatingLine && !editingLineId) {
      <div class="mx-6 my-4 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
        {{ lineModalErrorKey | translate }}
      </div>
    }

    @if (linesView$ | async; as linesView) {
      <div class="hidden md:block w-full overflow-x-auto">
        <table class="w-full text-sm table-fixed">
          <thead>
            <tr class="bg-neutral-50 dark:bg-neutral-800/50 border-b border-neutral-200 dark:border-neutral-800">
              <th class="pl-6 pr-2 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase w-auto">{{ 'COMPONENTS.SALES_ORDERS.LINES.NAME' | translate }}</th>
              <th class="px-2 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase hidden md:table-cell w-24">{{ 'COMPONENTS.SALES_ORDERS.LINES.TYPE' | translate }}</th>
              <th class="px-2 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase hidden lg:table-cell w-32">{{ 'COMPONENTS.SALES_ORDERS.LINES.SKU' | translate }}</th>
              <th class="px-2 py-3 text-right text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase w-28">{{ 'COMPONENTS.SALES_ORDERS.LINES.QUANTITY' | translate }}</th>
              <th class="px-2 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase hidden sm:table-cell w-28">{{ 'COMPONENTS.SALES_ORDERS.LINES.UNIT' | translate }}</th>
              <th class="px-2 py-3 text-right text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase hidden sm:table-cell w-28">{{ 'COMPONENTS.SALES_ORDERS.LINES.UNIT_PRICE' | translate }}</th>
              <th class="px-2 py-3 text-right text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase hidden md:table-cell w-24">{{ 'COMPONENTS.SALES_ORDERS.LINES.VAT' | translate }}</th>
              <th class="px-2 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase hidden md:table-cell w-32">{{ 'COMPONENTS.SALES_ORDERS.LINES.CURRENCY' | translate }}</th>
              <th class="py-3 text-right text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase w-32"
                  [class.pr-6]="isReadonly"
                  [class.pr-2]="!isReadonly"
                  [class.pl-2]="true">{{ 'COMPONENTS.SALES_ORDERS.LINES.SUM' | translate }}</th>
              @if (!isReadonly) {
                <th class="pl-2 pr-6 py-3 text-right text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase w-24">{{ 'COMPONENTS.SALES_ORDERS.LINES.ACTIONS' | translate }}</th>
              }
            </tr>
          </thead>

          <tbody [formGroup]="lineForm">
            <ng-template #lineEditRow let-sku="sku" let-typeBadge="typeBadge">
              <tr class="bg-blue-50/40 dark:bg-blue-900/10 border-t border-blue-200 dark:border-blue-800">
                <td class="pl-6 pr-2 py-2 align-middle min-w-0 max-w-0 overflow-hidden">
                  <app-search
                    class="block w-full min-w-0"
                    customClass="form-input--compact"
                    formControlName="inventoryItemId"
                    [items]="inventoryItemOptions"
                    [filterFn]="inventoryItemFilterFn"
                    [formatter]="inventoryItemFormatter"
                    [compareFn]="inventoryItemCompareFn"
                    [placeholderText]="'COMPONENTS.SALES_ORDERS.PLACEHOLDERS.INVENTORY_ITEM' | translate"
                    [errorMessage]="'COMPONENTS.SALES_ORDERS.ERRORS.INVALID_INVENTORY_ITEM' | translate"
                    [resultTemplate]="inventoryItemOptionTemplate"
                  ></app-search>
                  <ng-template #inventoryItemOptionTemplate let-r="result" let-t="term">
                    @if (r) {
                      <div class="flex flex-col">
                        <ngb-highlight class="font-semibold text-neutral-900 dark:text-neutral-50" highlightClass="hover-highlight" [result]="r.title" [term]="t"></ngb-highlight>
                        @if (r.subtitle) {
                          <ngb-highlight class="text-xs text-neutral-500 dark:text-neutral-400" highlightClass="hover-highlight" [result]="r.subtitle" [term]="t"></ngb-highlight>
                        }
                      </div>
                    }
                  </ng-template>
                </td>
                <td class="px-2 py-2 align-middle hidden md:table-cell">
                  @if (typeBadge) {
                    @if (isEditingServiceItem$ | async) {
                      <span class="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">{{ typeBadge | translate }}</span>
                    } @else {
                      <span class="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400">{{ typeBadge | translate }}</span>
                    }
                  } @else {
                    <span class="text-xs text-neutral-400 dark:text-neutral-500">—</span>
                  }
                </td>
                <td class="px-2 py-2 align-middle font-mono text-xs text-neutral-500 dark:text-neutral-400 hidden lg:table-cell truncate">{{ sku || '—' }}</td>
                <td class="px-2 py-2 align-middle text-right min-w-0">
                  <input
                    type="number"
                    min="0.0001"
                    step="0.0001"
                    class="form-input form-input--compact text-right tabular-nums"
                    formControlName="quantity"
                    [class.is-invalid]="lineForm.controls.quantity.touched && lineForm.controls.quantity.invalid"
                  />
                </td>
                <td class="px-2 py-2 align-middle hidden sm:table-cell min-w-0">
                  <select
                    class="form-input form-input--compact"
                    formControlName="unitOfMeasureId"
                    [class.is-invalid]="lineForm.controls.unitOfMeasureId.touched && lineForm.controls.unitOfMeasureId.invalid"
                  >
                    <option [ngValue]="null">{{ 'COMPONENTS.SALES_ORDERS.PLACEHOLDERS.UNIT_OF_MEASURE' | translate }}</option>
                    @for (unitOption of lineUnitOptions$ | async; track unitOption.unitOfMeasureId) {
                      <option [ngValue]="unitOption.unitOfMeasureId">
                        {{ unitOption.displayName }}
                        @if (unitOption.isBase) { ({{ 'COMPONENTS.SALES_ORDERS.FIELDS.BASE_UNIT_BADGE' | translate }}) }
                      </option>
                    }
                  </select>
                </td>
                <td class="px-2 py-2 align-middle text-right hidden sm:table-cell min-w-0">
                  <input
                    type="number"
                    min="0"
                    step="0.0001"
                    class="form-input form-input--compact text-right tabular-nums"
                    formControlName="unitPrice"
                    [class.is-invalid]="lineForm.controls.unitPrice.touched && lineForm.controls.unitPrice.invalid"
                  />
                </td>
                <td class="px-2 py-2 align-middle text-right hidden md:table-cell min-w-0">
                  <input
                    type="number"
                    min="0"
                    step="0.0001"
                    class="form-input form-input--compact text-right tabular-nums"
                    formControlName="taxPercent"
                    [class.is-invalid]="lineForm.controls.taxPercent.touched && lineForm.controls.taxPercent.invalid"
                  />
                </td>
                <td class="px-2 py-2 align-middle hidden md:table-cell min-w-0">
                  <select
                    class="form-input form-input--compact"
                    formControlName="currencyCode"
                    [attr.aria-label]="'COMPONENTS.SALES_ORDERS.LINES.CURRENCY' | translate"
                  >
                    <option [ngValue]="null">{{ 'COMPONENTS.SALES_ORDERS.LINES.CURRENCY_PLACEHOLDER' | translate }}</option>
                    @for (currency of currencyOptions; track currency) {
                      <option [ngValue]="currency">{{ currency }}</option>
                    }
                  </select>
                </td>
                <td class="pl-2 py-2 align-middle text-right font-semibold text-neutral-900 dark:text-neutral-50 whitespace-nowrap tabular-nums"
                    [class.pr-6]="isReadonly"
                    [class.pr-2]="!isReadonly">
                  @if (linePreview$ | async; as preview) {
                    {{ preview.amountExcludingTax | number: '1.2-2' }}
                  } @else { — }
                </td>
                @if (!isReadonly) {
                  <td class="pl-2 pr-6 py-2 align-middle">
                    <div class="flex gap-1.5 justify-end items-center">
                      <button
                        type="button"
                        class="inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-md border border-neutral-200 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        [disabled]="isSavingLine"
                        [title]="'BUTTONS.CANCEL' | translate"
                        [attr.aria-label]="'BUTTONS.CANCEL' | translate"
                        (click)="cancelEditing()"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        class="inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-md bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 text-white shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        [disabled]="isSavingLine || isLoadingItemDetails || lineForm.invalid"
                        [title]="'BUTTONS.SAVE' | translate"
                        [attr.aria-label]="'BUTTONS.SAVE' | translate"
                        (click)="saveLine()"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                    </div>
                  </td>
                }
              </tr>

              @if (lineAvailabilityView$ | async; as availability) {
                <tr class="bg-blue-50/40 dark:bg-blue-900/10">
                  <td [attr.colspan]="isReadonly ? 9 : 10" class="px-6 pb-3">
                    <div class="rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 px-2.5 py-1.5 text-xs">
                      <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-0.5">
                        <span class="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                          {{ 'COMPONENTS.SALES_ORDERS.FIELDS.STOCK_AVAILABLE' | translate }}
                        </span>
                        <span class="font-semibold text-neutral-900 dark:text-neutral-50 tabular-nums">
                          {{ availability.availableBaseQuantity | number: '1.2-2' }} {{ availability.baseUnitOfMeasureName }}
                        </span>
                      </div>
                      <div class="mt-0.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-0.5 text-[11px] text-neutral-500 dark:text-neutral-400">
                        <span class="tabular-nums">{{ 'COMPONENTS.SALES_ORDERS.FIELDS.STOCK_ON_HAND' | translate }}: {{ availability.stockBaseQuantity | number: '1.2-2' }}</span>
                        <span class="tabular-nums">{{ 'COMPONENTS.SALES_ORDERS.FIELDS.STOCK_RESERVED' | translate }}: {{ availability.reservedBaseQuantity | number: '1.2-2' }}</span>
                      </div>
                    </div>
                  </td>
                </tr>
              } @else if (isEditingServiceItem$ | async) {
                <tr class="bg-blue-50/40 dark:bg-blue-900/10">
                  <td [attr.colspan]="isReadonly ? 9 : 10" class="px-6 pb-3">
                    <div class="rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-2.5 py-1.5 text-xs">
                      <span class="inline-flex items-center gap-1 font-semibold text-emerald-700 dark:text-emerald-300">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                        {{ 'COMPONENTS.SALES_ORDERS.AVAILABILITY.ALWAYS_AVAILABLE' | translate }}
                      </span>
                    </div>
                  </td>
                </tr>
              }

              @if (lineModalErrorKey) {
                <tr>
                  <td [attr.colspan]="isReadonly ? 9 : 10" class="px-6 py-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">{{ lineModalErrorKey | translate }}</td>
                </tr>
              }
            </ng-template>

            @if (isCreatingLine) {
              <ng-container *ngTemplateOutlet="lineEditRow; context: { sku: createSku, typeBadge: createTypeBadge }"></ng-container>
            }

            @if (linesView.length === 0 && !isCreatingLine) {
              <tr>
                <td [attr.colspan]="isReadonly ? 9 : 10" class="px-6 py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
                  {{ 'COMPONENTS.SALES_ORDERS.EMPTY_LINES' | translate }}
                </td>
              </tr>
            }

            @for (line of linesView; track line.id) {
              @if (editingLineId === line.id) {
                <ng-container *ngTemplateOutlet="lineEditRow; context: { line: line, sku: line.sku, typeBadge: line.typeBadgeKey }"></ng-container>
              } @else {
                <tr
                  class="border-b border-neutral-100 dark:border-neutral-800 hover:bg-neutral-50/40 dark:hover:bg-neutral-800/40"
                  [class.bg-red-50]="line.shortfall !== null"
                  [class.dark:bg-red-900/20]="line.shortfall !== null"
                >
                  <td class="pl-6 pr-2 py-3">
                    <div class="flex items-start gap-2 min-w-0">
                      @if (line.composite; as composite) {
                        <button
                          type="button"
                          class="mt-0.5 inline-flex items-center justify-center w-5 h-5 shrink-0 rounded text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                          [attr.aria-label]="composite.toggleAriaLabelKey | translate"
                          [attr.aria-expanded]="composite.isExpanded"
                          (click)="toggleCompositeExpansion(line)"
                        >
                          <svg class="w-3.5 h-3.5 transition-transform"
                            [class.rotate-90]="composite.isExpanded"
                            fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      }
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-1.5 flex-wrap">
                          <span class="font-medium text-neutral-900 dark:text-neutral-50">{{ line.description || '-' }}</span>
                          @if (line.composite) {
                            <span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                              {{ 'COMPONENTS.GOODS.BADGES.COMPOSITE' | translate }}
                            </span>
                          }
                        </div>
                    @if (line.description2) {
                      <div class="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{{ line.description2 }}</div>
                    }
                    @if (line.shortfall) {
                      <div class="mt-1 text-xs font-semibold text-red-600 dark:text-red-400" role="alert">
                        {{
                          'COMPONENTS.SALES_ORDERS.MESSAGES.LINE_INSUFFICIENT_STOCK'
                            | translate
                              : {
                                  requested: line.shortfall.requestedBaseQuantity,
                                  available: line.shortfall.availableBaseQuantity
                                }
                        }}
                      </div>
                      @if (line.shortfall.components?.length) {
                        <ul class="mt-1 flex flex-col gap-0.5">
                          @for (shortChild of line.shortfall.components; track shortChild.childInventoryItemId) {
                            <li class="text-[11px] text-red-700 dark:text-red-300">
                              <span class="font-medium">{{ shortChild.childDisplayName || shortChild.childNumber || '—' }}</span>
                              <span class="text-neutral-500 dark:text-neutral-400">
                                ({{ 'COMPONENTS.SALES_ORDERS.MESSAGES.SHORT_CHILD' | translate: {
                                  required: shortChild.requiredBaseQuantity,
                                  available: shortChild.availableBaseQuantity
                                } }})
                              </span>
                            </li>
                          }
                        </ul>
                      }
                    }
                      </div>
                    </div>
                  </td>
                  <td class="px-2 py-3 hidden md:table-cell">
                    @if (line.typeBadgeKey) {
                      @if (line.isServiceLine) {
                        <span class="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">{{ line.typeBadgeKey | translate }}</span>
                      } @else {
                        <span class="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400">{{ line.typeBadgeKey | translate }}</span>
                      }
                    } @else {
                      <span class="text-xs text-neutral-400 dark:text-neutral-500">—</span>
                    }
                  </td>
                  <td class="px-2 py-3 font-mono text-xs text-neutral-500 dark:text-neutral-400 hidden lg:table-cell truncate">{{ line.sku }}</td>
                  <td class="px-2 py-3 text-right font-semibold text-neutral-900 dark:text-neutral-50 whitespace-nowrap">
                    {{ line.quantity | number: '1.0-2' }}
                    @if (line.showRemainingBadge) {
                      <span class="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 whitespace-nowrap" role="status">
                        {{ 'COMPONENTS.SALES_ORDERS.LINES.REMAINING_BADGE' | translate: { remaining: line.remaining } }}
                      </span>
                    }
                  </td>
                  <td class="px-2 py-3 text-xs text-neutral-500 dark:text-neutral-400 hidden sm:table-cell">{{ line.unitLabel }}</td>
                  <td class="px-2 py-3 text-right text-neutral-700 dark:text-neutral-300 hidden sm:table-cell whitespace-nowrap">{{ line.unitPrice | number: '1.2-2' }}</td>
                  <td class="px-2 py-3 text-right text-neutral-500 dark:text-neutral-400 hidden md:table-cell whitespace-nowrap">{{ line.taxPercent | number: '1.0-2' }}</td>
                  <td class="px-2 py-3 text-xs text-neutral-500 dark:text-neutral-400 hidden md:table-cell whitespace-nowrap">
                    @if (line.currencyCode) {
                      {{ line.currencyCode | currencySymbol }}
                    } @else {
                      —
                    }
                  </td>
                  <td class="pl-2 py-3 text-right font-semibold text-neutral-900 dark:text-neutral-50 whitespace-nowrap"
                      [class.pr-6]="isReadonly"
                      [class.pr-2]="!isReadonly">{{ line.amountExcludingTax | number: '1.2-2' }}</td>
                  @if (!isReadonly) {
                    <td class="pl-2 pr-6 py-3">
                      <div class="flex gap-1.5 justify-end items-center">
                        <button
                          type="button"
                          class="inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-md border border-neutral-200 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          [disabled]="isCreatingLine || !!editingLineId"
                          [title]="'BUTTONS.EDIT' | translate"
                          [attr.aria-label]="'BUTTONS.EDIT' | translate"
                          (click)="startEditLine(line.id)"
                        >
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          class="inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-md border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          [disabled]="isDeletingLineId === line.id || isCreatingLine || !!editingLineId"
                          [title]="'BUTTONS.REMOVE' | translate"
                          [attr.aria-label]="'BUTTONS.REMOVE' | translate"
                          (click)="deleteLine(line.id)"
                        >
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M10 7V4a1 1 0 011-1h2a1 1 0 011 1v3" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  }
                </tr>

                @if (line.composite?.isExpanded) {
                  @switch (line.composite?.state) {
                    @case ('loading') {
                      <tr class="bg-neutral-50/50 dark:bg-neutral-800/30 border-b border-neutral-50 dark:border-neutral-800/50">
                        <td [attr.colspan]="isReadonly ? 9 : 10" class="px-6 py-2 text-xs text-neutral-500 dark:text-neutral-400">{{ 'MESSAGES.LOADING' | translate }}</td>
                      </tr>
                    }
                    @case ('idle') {
                      <tr class="bg-neutral-50/50 dark:bg-neutral-800/30 border-b border-neutral-50 dark:border-neutral-800/50">
                        <td [attr.colspan]="isReadonly ? 9 : 10" class="px-6 py-2 text-xs text-neutral-500 dark:text-neutral-400">{{ 'MESSAGES.LOADING' | translate }}</td>
                      </tr>
                    }
                    @case ('error') {
                      <tr class="bg-neutral-50/50 dark:bg-neutral-800/30 border-b border-neutral-50 dark:border-neutral-800/50">
                        <td [attr.colspan]="isReadonly ? 9 : 10" class="px-6 py-2 text-xs text-red-600 dark:text-red-400">{{ 'COMPONENTS.SALES_ORDERS.ERRORS.ITEM_LOAD_FAILED' | translate }}</td>
                      </tr>
                    }
                    @case ('empty') {
                      <tr class="bg-neutral-50/50 dark:bg-neutral-800/30 border-b border-neutral-50 dark:border-neutral-800/50">
                        <td [attr.colspan]="isReadonly ? 9 : 10" class="px-6 py-2 text-xs text-neutral-500 dark:text-neutral-400">{{ 'COMPONENTS.SALES_ORDERS.LINES.COMPONENTS_EMPTY' | translate }}</td>
                      </tr>
                    }
                    @case ('ready') {
                      @for (child of line.composite!.children; track child.componentId) {
                        <tr class="border-b border-neutral-50 dark:border-neutral-800/50 bg-neutral-50/50 dark:bg-neutral-800/30">
                          <td class="pl-12 pr-2 py-2">
                            <div class="flex items-center gap-2">
                              <span class="w-px h-4 bg-indigo-300 dark:bg-indigo-700"></span>
                              <span class="text-xs text-neutral-600 dark:text-neutral-400 truncate">{{ child.displayName }}</span>
                            </div>
                          </td>
                          <td class="px-2 py-2 hidden md:table-cell">
                            <span class="px-1.5 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-500">{{ 'COMPONENTS.SALES_ORDERS.LINES.COMPONENT_BADGE' | translate }}</span>
                          </td>
                          <td class="px-2 py-2 font-mono text-xs text-neutral-400 dark:text-neutral-500 hidden lg:table-cell truncate">{{ child.sku }}</td>
                          <td class="px-2 py-2 text-right text-xs text-neutral-600 dark:text-neutral-400 whitespace-nowrap tabular-nums">{{ child.quantity | number: '1.0-4' }}</td>
                          <td class="px-2 py-2 text-xs text-neutral-400 dark:text-neutral-500 hidden sm:table-cell">{{ child.unitCode }}</td>
                          <td class="px-2 py-2 text-right text-xs text-neutral-500 dark:text-neutral-400 hidden sm:table-cell whitespace-nowrap tabular-nums">
                            @if (child.hasPrice) {
                              {{ child.unitPrice | number: '1.2-2' }}
                            } @else { — }
                          </td>
                          <td class="px-2 py-2 text-right text-xs text-neutral-400 dark:text-neutral-500 hidden md:table-cell">—</td>
                          <td class="px-2 py-2 text-xs text-neutral-500 dark:text-neutral-400 hidden md:table-cell whitespace-nowrap">
                            @if (child.currencyCode) {
                              {{ child.currencyCode | currencySymbol }}
                            } @else { — }
                          </td>
                          <td class="pl-2 py-2 text-right text-xs font-semibold text-neutral-600 dark:text-neutral-400 whitespace-nowrap tabular-nums"
                              [class.pr-6]="isReadonly"
                              [class.pr-2]="!isReadonly">
                            @if (child.hasPrice) {
                              {{ child.lineTotal | number: '1.2-2' }}
                            } @else { — }
                          </td>
                          @if (!isReadonly) {
                            <td class="pl-2 pr-6 py-2"></td>
                          }
                        </tr>
                      }
                    }
                  }
                }
              }
            }
          </tbody>

          @if (linesView.length > 0) {
            <tfoot class="bg-neutral-50 dark:bg-neutral-800/50 border-t border-neutral-200 dark:border-neutral-700">
              <tr>
                <td colspan="8" class="pl-6 pr-2 pt-3 pb-1 text-right text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                  {{ 'COMPONENTS.SALES_ORDERS.SUMMARY.SUBTOTAL' | translate }}:
                </td>
                <td class="pl-2 pt-3 pb-1 text-right text-sm font-medium text-neutral-700 dark:text-neutral-300 tabular-nums whitespace-nowrap"
                    [class.pr-6]="isReadonly"
                    [class.pr-2]="!isReadonly">
                  {{ order.totals.amountExcludingTax | number: '1.0-2' }} {{ 'COMPONENTS.SALES_ORDERS.SUMMARY.CURRENCY_SYMBOL' | translate }}
                </td>
                @if (!isReadonly) {
                  <td class="pl-2 pr-6 pt-3 pb-1"></td>
                }
              </tr>
              <tr>
                <td colspan="8" class="pl-6 pr-2 py-1 text-right text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                  {{ 'COMPONENTS.SALES_ORDERS.SUMMARY.VAT' | translate }}:
                </td>
                <td class="pl-2 py-1 text-right text-sm font-medium text-neutral-700 dark:text-neutral-300 tabular-nums whitespace-nowrap"
                    [class.pr-6]="isReadonly"
                    [class.pr-2]="!isReadonly">
                  {{ order.totals.totalTaxAmount | number: '1.0-2' }} {{ 'COMPONENTS.SALES_ORDERS.SUMMARY.CURRENCY_SYMBOL' | translate }}
                </td>
                @if (!isReadonly) {
                  <td class="pl-2 pr-6 py-1"></td>
                }
              </tr>
              <tr class="border-t border-neutral-200 dark:border-neutral-700">
                <td colspan="8" class="pl-6 pr-2 pt-2 pb-3 text-right text-sm font-bold text-neutral-900 dark:text-neutral-50">
                  {{ 'COMPONENTS.SALES_ORDERS.SUMMARY.GRAND_TOTAL' | translate }}
                </td>
                <td class="pl-2 pt-2 pb-3 text-right text-lg font-bold text-neutral-900 dark:text-neutral-50 tabular-nums whitespace-nowrap"
                    [class.pr-6]="isReadonly"
                    [class.pr-2]="!isReadonly">
                  {{ order.totals.amountIncludingTax | number: '1.0-2' }} {{ 'COMPONENTS.SALES_ORDERS.SUMMARY.CURRENCY_SYMBOL' | translate }}
                </td>
                @if (!isReadonly) {
                  <td class="pl-2 pr-6 pt-2 pb-3"></td>
                }
              </tr>
            </tfoot>
          }
        </table>
      </div>

      <!-- Mobile cards -->
      <div class="md:hidden" [formGroup]="lineForm">
        <ng-template #lineEditCardMobile let-sku="sku" let-typeBadge="typeBadge">
          <div class="sales-order-lines-mobile-edit-card border-t border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10 px-4 py-3 flex flex-col gap-3">
            <div class="flex items-center gap-2 flex-wrap">
              @if (typeBadge) {
                @if (isEditingServiceItem$ | async) {
                  <span class="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">{{ typeBadge | translate }}</span>
                } @else {
                  <span class="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400">{{ typeBadge | translate }}</span>
                }
              }
              @if (sku && sku !== '—') {
                <span class="font-mono text-xs text-neutral-500 dark:text-neutral-400">{{ sku }}</span>
              }
            </div>

            <app-search
              class="block w-full min-w-0"
              customClass="form-input--compact"
              formControlName="inventoryItemId"
              [items]="inventoryItemOptions"
              [filterFn]="inventoryItemFilterFn"
              [formatter]="inventoryItemFormatter"
              [compareFn]="inventoryItemCompareFn"
              [placeholderText]="'COMPONENTS.SALES_ORDERS.PLACEHOLDERS.INVENTORY_ITEM' | translate"
              [errorMessage]="'COMPONENTS.SALES_ORDERS.ERRORS.INVALID_INVENTORY_ITEM' | translate"
              [resultTemplate]="inventoryItemOptionTemplateMobile"
            ></app-search>
            <ng-template #inventoryItemOptionTemplateMobile let-r="result" let-t="term">
              @if (r) {
                <div class="flex flex-col">
                  <ngb-highlight class="font-semibold text-neutral-900 dark:text-neutral-50" highlightClass="hover-highlight" [result]="r.title" [term]="t"></ngb-highlight>
                  @if (r.subtitle) {
                    <ngb-highlight class="text-xs text-neutral-500 dark:text-neutral-400" highlightClass="hover-highlight" [result]="r.subtitle" [term]="t"></ngb-highlight>
                  }
                </div>
              }
            </ng-template>

            <div class="grid grid-cols-2 gap-x-3 gap-y-3">
              <label class="flex flex-col gap-1 text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                {{ 'COMPONENTS.SALES_ORDERS.LINES.QUANTITY' | translate }}
                <input
                  type="number"
                  min="0.0001"
                  step="0.0001"
                  class="form-input form-input--compact text-right tabular-nums"
                  formControlName="quantity"
                  [class.is-invalid]="lineForm.controls.quantity.touched && lineForm.controls.quantity.invalid"
                />
              </label>

              <label class="flex flex-col gap-1 text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                {{ 'COMPONENTS.SALES_ORDERS.LINES.UNIT' | translate }}
                <select
                  class="form-input form-input--compact"
                  formControlName="unitOfMeasureId"
                  [class.is-invalid]="lineForm.controls.unitOfMeasureId.touched && lineForm.controls.unitOfMeasureId.invalid"
                >
                  <option [ngValue]="null">{{ 'COMPONENTS.SALES_ORDERS.PLACEHOLDERS.UNIT_OF_MEASURE' | translate }}</option>
                  @for (unitOption of lineUnitOptions$ | async; track unitOption.unitOfMeasureId) {
                    <option [ngValue]="unitOption.unitOfMeasureId">
                      {{ unitOption.displayName }}
                      @if (unitOption.isBase) { ({{ 'COMPONENTS.SALES_ORDERS.FIELDS.BASE_UNIT_BADGE' | translate }}) }
                    </option>
                  }
                </select>
              </label>

              <label class="flex flex-col gap-1 text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                {{ 'COMPONENTS.SALES_ORDERS.LINES.UNIT_PRICE' | translate }}
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  class="form-input form-input--compact text-right tabular-nums"
                  formControlName="unitPrice"
                  [class.is-invalid]="lineForm.controls.unitPrice.touched && lineForm.controls.unitPrice.invalid"
                />
              </label>

              <label class="flex flex-col gap-1 text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                {{ 'COMPONENTS.SALES_ORDERS.LINES.VAT' | translate }}
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  class="form-input form-input--compact text-right tabular-nums"
                  formControlName="taxPercent"
                  [class.is-invalid]="lineForm.controls.taxPercent.touched && lineForm.controls.taxPercent.invalid"
                />
              </label>

              <label class="flex flex-col gap-1 text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400 col-span-2">
                {{ 'COMPONENTS.SALES_ORDERS.LINES.CURRENCY' | translate }}
                <select class="form-input form-input--compact" formControlName="currencyCode">
                  <option [ngValue]="null">{{ 'COMPONENTS.SALES_ORDERS.LINES.CURRENCY_PLACEHOLDER' | translate }}</option>
                  @for (currency of currencyOptions; track currency) {
                    <option [ngValue]="currency">{{ currency }}</option>
                  }
                </select>
              </label>
            </div>

            <div class="flex items-center justify-between gap-2 text-sm">
              <span class="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                {{ 'COMPONENTS.SALES_ORDERS.LINES.SUM' | translate }}
              </span>
              <span class="font-semibold text-neutral-900 dark:text-neutral-50 tabular-nums whitespace-nowrap">
                @if (linePreview$ | async; as preview) {
                  {{ preview.amountExcludingTax | number: '1.2-2' }}
                  @if (lineForm.controls.currencyCode.value) { {{ lineForm.controls.currencyCode.value }} }
                } @else { — }
              </span>
            </div>

            @if (lineAvailabilityView$ | async; as availability) {
              <div class="rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 px-2.5 py-1.5 text-xs">
                <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-0.5">
                  <span class="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                    {{ 'COMPONENTS.SALES_ORDERS.FIELDS.STOCK_AVAILABLE' | translate }}
                  </span>
                  <span class="font-semibold text-neutral-900 dark:text-neutral-50 tabular-nums">
                    {{ availability.availableBaseQuantity | number: '1.2-2' }} {{ availability.baseUnitOfMeasureName }}
                  </span>
                </div>
                <div class="mt-0.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-0.5 text-[11px] text-neutral-500 dark:text-neutral-400">
                  <span class="tabular-nums">{{ 'COMPONENTS.SALES_ORDERS.FIELDS.STOCK_ON_HAND' | translate }}: {{ availability.stockBaseQuantity | number: '1.2-2' }}</span>
                  <span class="tabular-nums">{{ 'COMPONENTS.SALES_ORDERS.FIELDS.STOCK_RESERVED' | translate }}: {{ availability.reservedBaseQuantity | number: '1.2-2' }}</span>
                </div>
              </div>
            } @else if (isEditingServiceItem$ | async) {
              <div class="rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-2.5 py-1.5 text-xs">
                <span class="inline-flex items-center gap-1 font-semibold text-emerald-700 dark:text-emerald-300">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {{ 'COMPONENTS.SALES_ORDERS.AVAILABILITY.ALWAYS_AVAILABLE' | translate }}
                </span>
              </div>
            }

            @if (lineModalErrorKey) {
              <div class="px-3 py-2 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
                {{ lineModalErrorKey | translate }}
              </div>
            }

            @if (!isReadonly) {
              <div class="flex gap-2 justify-end items-center">
                <button
                  type="button"
                  class="inline-flex items-center justify-center w-9 h-9 shrink-0 rounded-md border border-neutral-200 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  [disabled]="isSavingLine"
                  [title]="'BUTTONS.CANCEL' | translate"
                  [attr.aria-label]="'BUTTONS.CANCEL' | translate"
                  (click)="cancelEditing()"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <button
                  type="button"
                  class="inline-flex items-center justify-center w-9 h-9 shrink-0 rounded-md bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 text-white shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  [disabled]="isSavingLine || isLoadingItemDetails || lineForm.invalid"
                  [title]="'BUTTONS.SAVE' | translate"
                  [attr.aria-label]="'BUTTONS.SAVE' | translate"
                  (click)="saveLine()"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                </button>
              </div>
            }
          </div>
        </ng-template>

        @if (isCreatingLine) {
          <ng-container *ngTemplateOutlet="lineEditCardMobile; context: { sku: createSku, typeBadge: createTypeBadge }"></ng-container>
        }

        @if (linesView.length === 0 && !isCreatingLine) {
          <div class="px-6 py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
            {{ 'COMPONENTS.SALES_ORDERS.EMPTY_LINES' | translate }}
          </div>
        }

        @for (line of linesView; track line.id) {
          @if (editingLineId === line.id) {
            <ng-container *ngTemplateOutlet="lineEditCardMobile; context: { line: line, sku: line.sku, typeBadge: line.typeBadgeKey }"></ng-container>
          } @else {
            <div
              class="sales-order-lines-mobile-card border-t border-neutral-100 dark:border-neutral-800 px-4 py-3 flex flex-col gap-3"
              [class.bg-red-50]="line.shortfall !== null"
              [class.dark:bg-red-900/20]="line.shortfall !== null"
            >
              <div class="flex flex-col gap-1 min-w-0">
                <div class="flex items-start gap-2">
                  @if (line.composite; as composite) {
                    <button
                      type="button"
                      class="mt-0.5 inline-flex items-center justify-center w-5 h-5 shrink-0 rounded text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                      [attr.aria-label]="composite.toggleAriaLabelKey | translate"
                      [attr.aria-expanded]="composite.isExpanded"
                      (click)="toggleCompositeExpansion(line)"
                    >
                      <svg class="w-3.5 h-3.5 transition-transform"
                        [class.rotate-90]="composite.isExpanded"
                        fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  }
                  <div class="font-medium text-neutral-900 dark:text-neutral-50 break-words flex-1">{{ line.description || '-' }}</div>
                  @if (line.composite) {
                    <span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 shrink-0">
                      {{ 'COMPONENTS.GOODS.BADGES.COMPOSITE' | translate }}
                    </span>
                  }
                </div>
                @if (line.description2) {
                  <div class="text-xs text-neutral-500 dark:text-neutral-400 break-words">{{ line.description2 }}</div>
                }
                @if (line.shortfall) {
                  <div class="mt-1 text-xs font-semibold text-red-600 dark:text-red-400" role="alert">
                    {{
                      'COMPONENTS.SALES_ORDERS.MESSAGES.LINE_INSUFFICIENT_STOCK'
                        | translate
                          : {
                              requested: line.shortfall.requestedBaseQuantity,
                              available: line.shortfall.availableBaseQuantity
                            }
                    }}
                  </div>
                }
              </div>

              @if (line.typeBadgeKey || (line.sku && line.sku !== '—')) {
                <div class="flex items-center gap-2 flex-wrap">
                  @if (line.typeBadgeKey) {
                    @if (line.isServiceLine) {
                      <span class="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">{{ line.typeBadgeKey | translate }}</span>
                    } @else {
                      <span class="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400">{{ line.typeBadgeKey | translate }}</span>
                    }
                  }
                  @if (line.sku && line.sku !== '—') {
                    <span class="font-mono text-xs text-neutral-500 dark:text-neutral-400 break-all">{{ line.sku }}</span>
                  }
                </div>
              }

              <div class="grid grid-cols-2 gap-y-2 gap-x-3 text-sm">
                <div class="flex flex-col gap-0.5 min-w-0">
                  <span class="text-[11px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                    {{ 'COMPONENTS.SALES_ORDERS.LINES.QUANTITY' | translate }}
                  </span>
                  <span class="font-semibold text-neutral-900 dark:text-neutral-50 tabular-nums whitespace-nowrap">
                    {{ line.quantity | number: '1.0-2' }}
                    @if (line.showRemainingBadge) {
                      <span class="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 whitespace-nowrap" role="status">
                        {{ 'COMPONENTS.SALES_ORDERS.LINES.REMAINING_BADGE' | translate: { remaining: line.remaining } }}
                      </span>
                    }
                  </span>
                </div>

                <div class="flex flex-col gap-0.5 min-w-0">
                  <span class="text-[11px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                    {{ 'COMPONENTS.SALES_ORDERS.LINES.UNIT' | translate }}
                  </span>
                  <span class="text-neutral-700 dark:text-neutral-300 break-words">{{ line.unitLabel }}</span>
                </div>

                <div class="flex flex-col gap-0.5 min-w-0">
                  <span class="text-[11px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                    {{ 'COMPONENTS.SALES_ORDERS.LINES.UNIT_PRICE' | translate }}
                  </span>
                  <span class="text-neutral-700 dark:text-neutral-300 tabular-nums whitespace-nowrap">{{ line.unitPrice | number: '1.2-2' }}</span>
                </div>

                <div class="flex flex-col gap-0.5 min-w-0">
                  <span class="text-[11px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                    {{ 'COMPONENTS.SALES_ORDERS.LINES.VAT' | translate }}
                  </span>
                  <span class="text-neutral-700 dark:text-neutral-300 tabular-nums whitespace-nowrap">{{ line.taxPercent | number: '1.0-2' }}</span>
                </div>

                <div class="flex flex-col gap-0.5 min-w-0">
                  <span class="text-[11px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                    {{ 'COMPONENTS.SALES_ORDERS.LINES.CURRENCY' | translate }}
                  </span>
                  <span class="text-neutral-700 dark:text-neutral-300 whitespace-nowrap">
                    @if (line.currencyCode) {
                      {{ line.currencyCode | currencySymbol }}
                    } @else {
                      —
                    }
                  </span>
                </div>

                <div class="flex flex-col gap-0.5 min-w-0">
                  <span class="text-[11px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                    {{ 'COMPONENTS.SALES_ORDERS.LINES.SUM' | translate }}
                  </span>
                  <span class="font-semibold text-neutral-900 dark:text-neutral-50 tabular-nums whitespace-nowrap">{{ line.amountExcludingTax | number: '1.2-2' }}</span>
                </div>
              </div>

              @if (line.composite?.isExpanded) {
                <div class="rounded-md border border-indigo-200 dark:border-indigo-900/40 bg-indigo-50/40 dark:bg-indigo-900/10 px-3 py-2 text-xs">
                  @switch (line.composite?.state) {
                    @case ('loading') {
                      <div class="text-neutral-500 dark:text-neutral-400">{{ 'MESSAGES.LOADING' | translate }}</div>
                    }
                    @case ('idle') {
                      <div class="text-neutral-500 dark:text-neutral-400">{{ 'MESSAGES.LOADING' | translate }}</div>
                    }
                    @case ('error') {
                      <div class="text-red-600 dark:text-red-400">{{ 'COMPONENTS.SALES_ORDERS.ERRORS.ITEM_LOAD_FAILED' | translate }}</div>
                    }
                    @case ('empty') {
                      <div class="text-[10px] uppercase tracking-wider font-semibold text-neutral-500 dark:text-neutral-400 mb-1.5">
                        {{ 'COMPONENTS.SALES_ORDERS.LINES.COMPONENTS_TITLE' | translate }}
                      </div>
                      <div class="text-neutral-500 dark:text-neutral-400">{{ 'COMPONENTS.SALES_ORDERS.LINES.COMPONENTS_EMPTY' | translate }}</div>
                    }
                    @case ('ready') {
                      <div class="text-[10px] uppercase tracking-wider font-semibold text-neutral-500 dark:text-neutral-400 mb-1.5">
                        {{ 'COMPONENTS.SALES_ORDERS.LINES.COMPONENTS_TITLE' | translate }}
                      </div>
                      <ul class="flex flex-col gap-1.5">
                        @for (child of line.composite!.children; track child.componentId) {
                          <li class="flex flex-wrap items-baseline justify-between gap-2 border-t border-indigo-100 dark:border-indigo-900/40 pt-1.5 first:border-t-0 first:pt-0">
                            <div class="min-w-0">
                              <div class="text-neutral-800 dark:text-neutral-200 break-words">{{ child.displayName }}</div>
                              @if (child.sku && child.sku !== '—') {
                                <div class="font-mono text-[10px] text-neutral-500 dark:text-neutral-400 break-all">{{ child.sku }}</div>
                              }
                            </div>
                            <div class="font-semibold tabular-nums text-neutral-900 dark:text-neutral-50 whitespace-nowrap">
                              {{ child.quantity | number: '1.0-4' }}
                              <span class="font-normal text-neutral-500 dark:text-neutral-400 ml-1">{{ child.unitCode }}</span>
                            </div>
                          </li>
                        }
                      </ul>
                    }
                  }
                </div>
              }

              @if (!isReadonly) {
                <div class="flex gap-2 justify-end items-center">
                  <button
                    type="button"
                    class="inline-flex items-center justify-center w-9 h-9 shrink-0 rounded-md border border-neutral-200 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    [disabled]="isCreatingLine || !!editingLineId"
                    [title]="'BUTTONS.EDIT' | translate"
                    [attr.aria-label]="'BUTTONS.EDIT' | translate"
                    (click)="startEditLine(line.id)"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    class="inline-flex items-center justify-center w-9 h-9 shrink-0 rounded-md border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    [disabled]="isDeletingLineId === line.id || isCreatingLine || !!editingLineId"
                    [title]="'BUTTONS.REMOVE' | translate"
                    [attr.aria-label]="'BUTTONS.REMOVE' | translate"
                    (click)="deleteLine(line.id)"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M10 7V4a1 1 0 011-1h2a1 1 0 011 1v3" />
                    </svg>
                  </button>
                </div>
              }
            </div>
          }
        }

        @if (linesView.length > 0) {
          <div class="sales-order-lines-mobile-totals border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50">
            <div class="flex items-center justify-between px-4 pt-3 pb-1">
              <span class="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                {{ 'COMPONENTS.SALES_ORDERS.SUMMARY.SUBTOTAL' | translate }}
              </span>
              <span class="text-sm font-medium text-neutral-700 dark:text-neutral-300 tabular-nums whitespace-nowrap">
                {{ order.totals.amountExcludingTax | number: '1.0-2' }} {{ 'COMPONENTS.SALES_ORDERS.SUMMARY.CURRENCY_SYMBOL' | translate }}
              </span>
            </div>
            <div class="flex items-center justify-between px-4 py-1">
              <span class="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                {{ 'COMPONENTS.SALES_ORDERS.SUMMARY.VAT' | translate }}
              </span>
              <span class="text-sm font-medium text-neutral-700 dark:text-neutral-300 tabular-nums whitespace-nowrap">
                {{ order.totals.totalTaxAmount | number: '1.0-2' }} {{ 'COMPONENTS.SALES_ORDERS.SUMMARY.CURRENCY_SYMBOL' | translate }}
              </span>
            </div>
            <div class="flex items-center justify-between px-4 pt-2 pb-3 border-t border-neutral-200 dark:border-neutral-700">
              <span class="text-sm font-bold text-neutral-900 dark:text-neutral-50">
                {{ 'COMPONENTS.SALES_ORDERS.SUMMARY.GRAND_TOTAL' | translate }}
              </span>
              <span class="text-lg font-bold text-neutral-900 dark:text-neutral-50 tabular-nums whitespace-nowrap">
                {{ order.totals.amountIncludingTax | number: '1.0-2' }} {{ 'COMPONENTS.SALES_ORDERS.SUMMARY.CURRENCY_SYMBOL' | translate }}
              </span>
            </div>
          </div>
        }
      </div>
    }

  </app-card-shell>
}
